import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './SupabaseAuthContext';

const DarkModeContext = createContext();

const DARK_MODE_KEY = 'footparadise_dark_mode';

// Preferences are stored per-account (keyed by user id) so switching who's
// logged in on the same device/browser never leaks one account's look into
// another's — 'guest' covers the logged-out state (Login/Signup screens).
function readStored(userId) {
  return localStorage.getItem(`${DARK_MODE_KEY}::${userId}`) === 'true';
}

export function DarkModeProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || 'guest';
  const [isDarkMode, setIsDarkMode] = useState(() => readStored(userId));

  // Re-derive from storage whenever the logged-in account changes.
  useEffect(() => {
    setIsDarkMode(readStored(userId));
  }, [userId]);

  useEffect(() => {
    localStorage.setItem(`${DARK_MODE_KEY}::${userId}`, isDarkMode.toString());

    // Apply dark mode class to body for global CSS
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode, userId]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  // Dark mode color palette
  const darkTheme = {
    // Backgrounds
    background: '#000000',
    backgroundSecondary: '#111111',
    backgroundTertiary: '#1a1a1a',

    // Text
    text: '#ffffff',
    textSecondary: '#cccccc',
    textMuted: '#888888',

    // Borders
    border: '#333333',
    borderLight: '#444444',

    // Buttons
    buttonPrimary: '#ffffff',
    buttonPrimaryText: '#000000',
    buttonSecondary: '#222222',
    buttonSecondaryText: '#ffffff',

    // Cards
    card: '#111111',
    cardBorder: '#333333',

    // Header
    header: '#000000',
    headerBorder: '#333333',

    // Overlays
    overlay: 'rgba(0, 0, 0, 0.9)',

    // Keep accent colors for highlights
    accent: '#FFD700',
  };

  const lightTheme = {
    // These are just markers, actual light mode uses original styles
    background: 'transparent',
    text: '#ffffff',
    useWallpaper: true,
  };

  const theme = isDarkMode ? darkTheme : lightTheme;

  return (
    <DarkModeContext.Provider value={{ isDarkMode, toggleDarkMode, theme }}>
      {children}
    </DarkModeContext.Provider>
  );
}

export function useDarkMode() {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
}

export default DarkModeContext;
